import style from "./FormDropdown.module.css";

import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Label from "./Label";
import InputError from "./InputError";
import Icon from "@/ui/icon/Icon";
import FlagIcon from "./FlagIcon";

import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from "@/ui/dropdownMenu/DropdownMenu";

export default function FormDropdown({
	name,
	required,
	notAllowed,
	label,
	options = [],
	placeholder,
	...props
}) {
	const { t } = useTranslation();
	const {
		register,
		formState: { errors, isSubmitting },
		setValue,
		getValues,
		watch,
	} = useFormContext();

	// register and ensure default 'BA' if no value present
	useEffect(() => {
		register(name, { required });
		const current = getValues(name);
		if (!current) {
			setValue(name, "BA", { shouldDirty: false, shouldTouch: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [name]);

	const currentValue = watch(name);

	const getLabelFor = (val) => {
		const opt = options.find((o) =>
			typeof o === "string" ? o === val : o.value === val
		);
		return opt ? (typeof opt === "string" ? opt : opt.label) : val;
	};

	return (
		<div className={style.holder}>
			{label && (
				<Label required={required} htmlFor={name}>
					{label}
				</Label>
			)}

			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						id={name}
						className={`${style.input}`}
						disabled={isSubmitting || notAllowed}
						aria-haspopup="listbox"
						aria-expanded="false"
						{...props}
					>
						<span className={style.triggerContent}>
							<span className={style.flag}>
								<FlagIcon code={currentValue} />
							</span>
							<span className={style.labelText}>
								{currentValue
									? getLabelFor(currentValue)
									: placeholder ?? t("common:actions.select")}
							</span>
						</span>
						<span className={style.caret} aria-hidden>
							<Icon icon="fa-caret-down" />
						</span>
					</button>
				</DropdownMenuTrigger>

				<DropdownMenuContent className={style.content} sideOffset={6}>
					{options.map((opt) => {
						const value = typeof opt === "string" ? opt : opt.value;
						const labelText =
							typeof opt === "string" ? opt : opt.label;
						const isSelected = value === (currentValue ?? "BA");
						return (
							<DropdownMenuItem
								key={value}
								onSelect={() => {
									setValue(name, value, {
										shouldDirty: true,
										shouldTouch: true,
									});
								}}
								className={`${style.item} ${
									isSelected ? style.selected : ""
								}`}
							>
								<span className={style.flag}>
									<FlagIcon code={value} />
								</span>
								<span className={style.itemLabel}>
									{labelText}
								</span>
								{isSelected && (
									<span className={style.check}>
										<Icon icon="fa-check" />
									</span>
								)}
							</DropdownMenuItem>
						);
					})}
				</DropdownMenuContent>
			</DropdownMenu>

			{errors[name] && <InputError error={errors[name]} />}
		</div>
	);
}
// ...existing code...
